import { AgGridReact } from 'ag-grid-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useExperimentSelector } from '../../../../store/selectors/experimentSelector'
import { useCommonApi } from '../../../../store/api/hooks/commonApiHook'
import { useExperimentApi } from '../../../../store/api/hooks/experimentApiHook'
import { useFpLoader } from '../../../../store/api/hooks/fpLoaderHook'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { BusinessFunctionDataType, DistributionColumnNameType } from '../../../../_config/types'
import { ErrorResponse } from '../../../../store/api/apiTypes'
import toast from 'react-hot-toast'
import Text from '../../../../elements/Typo/Text'
import Dropdown from '../../../../elements/Form/dropdown'
import CustomAgGrid from '../../../../components/CustomAgGrid'
import Label from '../../../../elements/formLabel'
import Button from '../../../../elements/button'
import CommonInputText from '../../../../components/commonInputText'
import { createOptionsFromStringArray, getSelectedOption, trackEvent } from '../../../../_common/functions'
import Loader from '../../../../components/loader'
import MeasurementSuccessModal from '../../MeasurementConfigure/modal/measurementSuccess.modal'
import FieldValidation from '../../../../components/groupConfigComponents/fieldValidation'
import i18n from '../../../../i18n'
import { DateFrequency } from '../../MeasurementConfigure'
import { EXPERIMENT_TYPES } from '../../../../_common/constant'
import P from '../../../../elements/Typo/P'
import { cellRenderer } from '../../../../components/cellRenderer'
import { useMeasurementConfigure } from '../../../../_common/hook/useMeasurementConfigure'

interface Props {
    tabledata: any
    literal: string
    onContinue: () => void
    onPrevious: () => void
}

type ColumnOption = { label: string; value: string }
export type ColumnFormat = 'option' | 'string'
export type ColumnKeys = DistributionColumnNameType[] | string
export type ColumnsMap = Partial<Record<DistributionColumnNameType, string[]>>

const toColumnsMap = (data: any): ColumnsMap => {
    const _columns: ColumnsMap = {}
    Object.keys(data ?? {}).forEach((key: string) => {
        const _names = data[key]
        _columns[key as DistributionColumnNameType] = Array.isArray(_names) ? _names : []
    })
    return _columns
}

export const getColumnNames = (columns: ColumnsMap | null, keys: ColumnKeys = 'all'): string[] => {
    const _requested = (Array.isArray(keys) ? keys : keys.split(',')).map((key: string) => key.trim()).filter(Boolean)
    const _keys = _requested.length === 0 || _requested.includes('all') ? Object.keys(columns ?? {}) : _requested
    const _names = new Set<string>()
    _keys.forEach((key: string) => {
        const _groupNames = columns?.[key as DistributionColumnNameType] ?? []
        _groupNames.forEach((name: string) => {
            if (name) {
                _names.add(name)
            }
        })
    })
    return Array.from(_names).sort((a: string, b: string) => a.localeCompare(b))
}

export const getColumnsFor = <T extends ColumnFormat = 'option'>(
    columns: ColumnsMap | null,
    keys: ColumnKeys = 'all',
    common = false,
    format: T = 'option' as T,
    otherColumns: ColumnsMap | null = null,
): T extends 'string' ? string[] : ColumnOption[] => {
    let _names = getColumnNames(columns, keys)
    if (common) {
        // keep only columns that also exist in the other table
        const _shared = new Set(getColumnNames(otherColumns, 'all'))
        _names = _names.filter((name: string) => _shared.has(name))
    }
    return (format === 'string' ? _names : createOptionsFromStringArray(_names)) as T extends 'string' ? string[] : ColumnOption[]
}

const MTEMeasurementConfigure: React.FC<Props> = ({ tabledata, literal, onContinue, onPrevious }) => {
    const { t } = useTranslation()
    const params: any = useParams()
    const dataTableGrid = useRef<AgGridReact>(null)
    const experiment = useExperimentSelector()
    const conmmonApi = useCommonApi()
    const experimentApi = useExperimentApi()
    const [KPIRowData, setKPIRowData] = useState<[] | null>(null)
    const [KPIColumnDefs, setKPIColumnDefs] = useState<[]>([])
    const [InterventionRowData, setInterventionRowData] = useState<[] | null>(null)
    const [InterventionColumnDefs, setInterventionColumnDefs] = useState<[]>([])
    const { showLoader, hideLoader } = useFpLoader()
    const [KPIColumns, setKPIColumns] = useState<ColumnsMap | null>(null)
    const [InterventionColumns, setInterventionColumns] = useState<ColumnsMap | null>(null)

    const [kpiInterests, setKpiInterests] = useState<any>(null)
    // const [impact, setImpact] = useState<boolean>(true)
    const [working, setWorking] = useState<boolean>(false)
    const [mdata, setMdata] = useState<any>(null)

    const measurementHookFunction = useMeasurementConfigure({ experimentType: 'RCT' })

    const measurementSuccessModalRef: any = useRef()

    const {
        control,
        getValues,
        trigger,
        formState: { errors },
    } = useForm()

    const {
        fields: metricFields,
        append: metricAppend,
        remove: metricRemove,
    } = useFieldArray({
        control,
        name: 'metrics',
    })

    const {
        fields: interventions,
        append: interventionsAppend,
        remove: interventionsRemove,
    } = useFieldArray({
        control,
        name: 'interventions',
    })

    const getKpiColumns = <T extends ColumnFormat = 'option'>(keys: ColumnKeys = ['all'], common = false, format: T = 'option' as T) => getColumnsFor(KPIColumns, keys, common, format, InterventionColumns)

    const getInterventionColumns = <T extends ColumnFormat = 'option'>(keys: ColumnKeys = ['all'], common = false, format: T = 'option' as T) => getColumnsFor(InterventionColumns, keys, common, format, KPIColumns)

    const fetchKPIColumns = () => {
        showLoader()
        experimentApi.getColumnsForGroupDristribution(
            {
                literal: params.experiment,
                tableType: 'KPI',
            },
            (resp: any) => {
                setKPIColumns(toColumnsMap(resp?.data))
                hideLoader()
            },
            (error: any) => {
                hideLoader()
            },
        )
    }

    const fetchInterventionColumns = () => {
        experimentApi.getColumnsForGroupDristribution(
            {
                literal: params.experiment,
                tableType: 'Intervention',
            },
            (resp: any) => {
                setInterventionColumns(toColumnsMap(resp?.data))
            },
            (error: any) => {},
        )
    }

    const createColumnDes = (data: any) => {
        return data.map((key: any, i: number) => {
            return {
                headerName: key,
                field: key,
                filter: false,
                sortable: false,
                suppressMenu: true,
                cellRenderer,
            }
        })
    }

    const [kapiTableFatched, setKapiTableFatched] = useState(false)
    const [interventionTableFatched, setInterventionTableFatched] = useState(false)

    const getTableData = (type: 'KPI' | 'Intervention') => {
        return new Promise((resolve, reject) => {
            if (type === 'KPI') {
                setKapiTableFatched(true)
            } else if (type === 'Intervention') {
                setInterventionTableFatched(true)
            }

            measurementHookFunction
                .getTableData(experiment, type)
                .then((data: any) => {
                    const columnDefs: any = createColumnDes(Object.keys(data[0]))
                    if (type === 'KPI') {
                        setKPIColumnDefs(columnDefs)
                        setKPIRowData(data)
                    } else if (type === 'Intervention') {
                        setInterventionColumnDefs(columnDefs)
                        setInterventionRowData(data)
                    }
                    resolve(data)
                })
                .catch((error: any) => {
                    reject(new Error(error?.message || 'An unknown error occurred'))
                })
        })
    }

    const getKpiTypes = () => {
        conmmonApi.getKpiTypes(
            { sort_by: '-created_at', page: 1, per_page: 10 },
            (data: any) => {
                const interests = structuredClone(data.data.result)?.map((kpi: any) => {
                    kpi.label = kpi.name
                    kpi.value = kpi.kpi_literal
                    return kpi
                })
                setKpiInterests(interests)
            },
            (error: ErrorResponse<BusinessFunctionDataType>) => {
                toast.error(error.message)
            },
        )
    }

    const executeDesignMergeOverview = () => {
        showLoader()
        experimentApi.executedesignmergeoverview(
            { literal: params.experiment },
            (resp: any) => {
                hideLoader()
                setMdata(resp)
                measurementSuccessModalRef?.current?.showModal()
                if (onContinue) {
                    onContinue()
                }
            },
            (error: any) => {
                hideLoader()
            },
        )
    }

    const executeMTEOverview = () => {
        showLoader()
        experimentApi.mtesubmit(
            { literal: params.experiment },
            (resp: any) => {
                setMdata(resp)
                measurementSuccessModalRef?.current?.showModal()
                setWorking(false)
                hideLoader()
                if (onContinue) {
                    onContinue()
                }
            },
            (error: any) => {
                setWorking(false)
                hideLoader()
            },
        )
    }

    const updateExperiment = () => {
        const DATA: any = getValues()

        const payload: any = {
            literal,
            measurement_column_map: {
                granularity: DATA.granularity.value,
                date: DATA.date.value,
                date_frequency: DATA.frequency,
                allocation_group: '',
                blocking_factors: DATA.blockingFactors?.map((option: ColumnOption) => option.value) ?? [],
                //breakdown_level: DATA.breakdown ? DATA.breakdown.value : null,
                kpi: DATA.metrics.map((kpi: any) => {
                    const kpidata = {
                        ...kpi,
                        column_map: kpi.column_map.value,
                    }
                    delete kpidata.id
                    return kpidata
                }),
            },
            submit_from: 'measurement_configuration',

            intervention_column_map: {
                granularity: DATA.interventionGranularity.value,
                tp_cols: DATA.touchPoints.map((tp: any) => tp.value),
            },
        }
        showLoader()
        setWorking(true)
        experimentApi.updateExperiment(
            payload,
            () => {
                if (experiment?.experiment_type?.title == EXPERIMENT_TYPES.MTE.title) {
                    executeMTEOverview()
                } else {
                    executeDesignMergeOverview()
                }
            },
            (error: any) => {
                toast.error(error?.message)
                setWorking(false)
                hideLoader()
            },
        )
    }

    useEffect(() => {
        if (experiment?.exp_interventions) {
            interventionsRemove()
            experiment?.exp_interventions.map((intervention: any) => {
                interventionsAppend({ name: intervention })
            })
        }
        if (
            (experiment?.data_mappings?.delta_measurement_table_name || experiment?.data_mappings?.user_created_measurement_table_name) &&
            (experiment?.data_mappings?.delta_intervention_table_name || experiment?.data_mappings?.user_created_intervention_table_name)
        ) {
            if (!kapiTableFatched || !interventionTableFatched) {
                void getTableData('KPI')
                void getTableData('Intervention')
                fetchKPIColumns()
                fetchInterventionColumns()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experiment])

    useEffect(() => {
        if (kpiInterests && experiment) {
            if (experiment?.experiment_metadatas?.metrices) {
                metricRemove()
                experiment?.experiment_metadatas?.metrices.map((kpi: any) => {
                    metricAppend(kpi)
                })
            }
        } else {
            getKpiTypes()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experiment, kpiInterests])

    return (
        <div data-testid="mte-measurement-configure-root">
            <div className="flex items-center justify-between">
                <div className="relative flex items-center gap-2">
                    <div className="absolute -left-5 top-0 h-full w-2 bg-primary rounded-e-md"></div>
                    <P className="!mb-0 font-semibold">{experiment?.experiment_name}</P>
                </div>
            </div>
            <p data-testid="header-configure-data" className="font-bold text-start text-[1.125rem] leading-8">
                {t('Configure Data')}
            </p>

            <Text size="lg1" className="my-2 text-center font-semibold" data-testid="subheader-configure-groups">
                {t('Configure Groups')}
            </Text>

            <div data-testid="section-granularity-breakdown-date">
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <Text size="15" className="text-black font-medium" data-testid="label-granularity">
                            {t('Select Granularity')}
                        </Text>
                        <Text size="15" className="!text-[#464E5F] mb-2 italic" data-testid="desc-granularity">
                            {t('Please select the column which represents the Granularity')} <span className="text-red-500">*</span>
                        </Text>
                    </div>

                    {/* <div>
                        <Text size="15" className="text-black font-medium" data-testid="label-breakdown-level">
                            {t('Select Breakdown Level')}
                        </Text>
                        <Text size="15" className="!text-[#464E5F] mb-2 italic" data-testid="desc-breakdown-level">
                            {t('Please select the column which represents the Breakdown Level. Ex: Brand')}
                        </Text>
                    </div> */}

                    <div>
                        <Text size="15" className="text-black font-medium" data-testid="label-date">
                            {t('Select Date')}
                        </Text>
                        <Text size="15" className="!text-[#464E5F] mb-2 italic" data-testid="desc-date">
                            {t('Please select the column which represents the date')} <span className="text-red-500">*</span>
                        </Text>
                    </div>
                    <div>
                        <Text size="md" className="text-black font-medium" data-testid="label-select-kpis-column">
                            {t('Select KPIs Column')}
                        </Text>
                        <Text size="md" className="text-[#464E5F] mb-2 italic" data-testid="desc-select-kpis-column">
                            {t('The columns which represent the primary and other KPIs on which you want to execute Measurement. Please ensure to select the relevant numerical columns only.')} <span className="text-red-500">*</span>
                        </Text>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-3" data-testid="inputs-granularity-breakdown-date">
                    <div>
                        <Controller
                            name="granularity"
                            control={control}
                            rules={{ required: t('Please select') }}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    value={value}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('granularity')
                                        trackEvent('measurement_mte_configure_kpi_granularity_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                    }}
                                    options={getKpiColumns(['string_columns', 'numeric_columns'])}
                                    error={errors?.granularity?.message as string}
                                    data-testid="input-granularity-dropdown"
                                />
                            )}
                        />

                        <FieldValidation />
                    </div>

                    {/* <div>
                        <Controller
                            name="breakdown"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    value={value}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('breakdown')
                                        trackEvent('measurement_mte_configure_kpi_breakdown_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                    }}
                                    options={getKpiColumns(['all'])}
                                    error={errors?.breakdown?.message as string}
                                    data-testid="input-breakdown-dropdown"
                                />
                            )}
                        />

                        <FieldValidation />
                    </div> */}

                    <div>
                        <Controller
                            name="date"
                            control={control}
                            rules={{ required: t('Please select') }}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    value={value}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('date')
                                        trackEvent('measurement_mte_configure_kpi_date_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                    }}
                                    options={getKpiColumns(['datetime_columns'])}
                                    error={errors?.date?.message as string}
                                    data-testid="input-date-dropdown"
                                />
                            )}
                        />

                        <div className="bg-[#FAF8EF] border border-[#0AD4E133] rounded-[0.45rem] mt-2" data-testid="validation-date">
                            <div className="flex justify-between p-2">
                                <Text size="lg" className="font-semibold">
                                    {t('Validation:')} {t('No Missing Values')}
                                </Text>
                            </div>
                            <div className="border-b border-[#0AD4E133]"></div>
                            <div className="flex items-center justify-between p-1">
                                <Text size="md" className="!leading-none">
                                    {t('Select Time Granularity')}
                                </Text>
                                {DateFrequency(control, trigger, metricFields, t)}
                            </div>
                            <div className="flex justify-end" data-testid="frequency-error-message">
                                {errors?.frequency?.message ? <p className="text-error text-[.542rem] mt-1">{errors?.frequency?.message as string}</p> : null}
                            </div>
                        </div>
                    </div>
                    <div>
                        {kpiInterests && (
                            <>
                                {metricFields.map((kpi: any, i: number) => (
                                    <div key={`kpi-${i.toString()}`} className="grid grid-cols-2 gap-2 mb-2" data-testid={`kpi-row-${i}`}>
                                        <CommonInputText value={getSelectedOption(kpi.kpi_literal, kpiInterests)?.label} disabled={true} data-testid={`kpi-label-${i}`} />
                                        <Controller
                                            name={`metrics.${i}.column_map`}
                                            control={control}
                                            rules={{
                                                required: t('Please select'),
                                            }}
                                            render={({ field: { value, onChange } }) => (
                                                <Dropdown
                                                    value={value}
                                                    onChange={(value: any) => {
                                                        onChange(value)
                                                        void trigger(`metrics.${i}`)
                                                        trackEvent('measurement_mte_configure_kpi_mapped', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                                    }}
                                                    options={getKpiColumns(['numeric_columns'])}
                                                    error={(errors?.metrics as any)?.[i]?.column_map?.message as string}
                                                    data-testid={`kpi-column-dropdown-${i}`}
                                                />
                                            )}
                                        />
                                    </div>
                                ))}
                            </>
                        )}
                    </div>

                    {/* kpiInterests && (
                        <div className="mt-4 grid grid-cols-3 gap-3">
                            <div>
                                <div>
                                    <Text size="md" className="text-black font-medium" data-testid="label-select-kpis-column">
                                        {t('Select KPIs Column')}
                                    </Text>
                                    <Text size="md" className="text-[#464E5F] mb-2 italic" data-testid="desc-select-kpis-column">
                                        {t('The columns which represent the primary and other KPIs on which you want to execute Measurement. Please ensure to select the relevant numerical columns only.')} <span className="text-red-500">*</span>
                                    </Text>
                                </div>

                                {kpiInterests && (
                                    <>
                                        {metricFields.map((kpi: any, i: number) => (
                                            <div key={`kpi-${i.toString()}`} className="grid grid-cols-2 gap-2 mb-2" data-testid={`kpi-row-${i}`}>
                                                <CommonInputText value={getSelectedOption(kpi.kpi_literal, kpiInterests)?.label} disabled={true} data-testid={`kpi-label-${i}`} />
                                                <Controller
                                                    name={`metrics.${i}.column_map`}
                                                    control={control}
                                                    rules={{
                                                        required: t('Please select'),
                                                    }}
                                                    render={({ field: { value, onChange } }) => (
                                                        <Dropdown
                                                            value={value}
                                                            onChange={(value: any) => {
                                                                onChange(value)
                                                                void trigger(`metrics.${i}`)
                                                                trackEvent('measurement_mte_configure_kpi_mapped', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                                            }}
                                                            options={getKpiColumns(['numeric_columns'])}
                                                            error={(errors?.metrics as any)?.[i]?.column_map?.message as string}
                                                            data-testid={`kpi-column-dropdown-${i}`}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                            { <div>
                        <div>
                            <Text size="md" className="text-black font-medium">
                                {t('Select Focus Brand')}
                            </Text>
                            <Text size="md" className="text-[#464E5F] mb-2 italic">
                                {t('Please select the column which represents the Breakdown Level. Ex: Brand.')} <span className="text-red-500">*</span>
                            </Text>
                        </div>

                        <div>
                            <Controller
                                name="focus_brand"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Dropdown
                                        value={value}
                                        onChange={(value: any) => {
                                            onChange(value)
                                            void trigger('focus_brand')
                                            trackEvent('measurement_mte_configure_focus_brand_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                        }}
                                        options={getKpiColumns(['all'])}
                                        error={errors?.breakdown?.message as string}
                                    />
                                )}
                            />
                        </div>
                    </div> */}
                    {/* <div>
                        <div>
                            <Text size="md" className="text-black font-medium">
                                {t('Select Grouping Column(s)')}
                            </Text>
                            <Text size="md" className="text-[#464E5F] mb-2 italic">
                                {t('Select the column that maps Test and Control groups at the chosen granularity.')} <span className="text-red-500">*</span>
                            </Text>
                        </div>

                        <div>
                            <Controller
                                name="group_column"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Dropdown
                                        value={value}
                                        onChange={(value: any) => {
                                            onChange(value)
                                            void trigger('group_column')
                                            trackEvent('measurement_mte_configure_grouping_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                        }}
                                        options={getKpiColumns(['all'])}
                                        error={errors?.breakdown?.message as string}
                                    />
                                )}
                            />
                        </div>
                    </div> }
                        </div>
                    ) */}
                </div>
            </div>

            <p className="text-[1rem] font-bold mt-4" data-testid="header-kpi-data">
                {t('KPI data')}
            </p>

            <div className="mt-1 mb-4" data-testid="kpi-data-grid">
                <CustomAgGrid
                    key={'mte1_' + i18n.language}
                    gridRef={dataTableGrid}
                    rowData={KPIRowData}
                    columnDefs={KPIColumnDefs}
                    defaultColDef={{ sortable: true, filter: true }}
                    theme="light"
                    pagination={false}
                    paginationPageSize={5}
                    paginationPageSizeSelector={[5, 10, 20, 50]}
                    style={{ height: 350, width: '100%' }}
                    gridId="summmyGrid"
                    rowHeight={50}
                    alternativeRowColor={'#E5B61112'}
                    data-testid="kpi-custom-ag-grid"
                />
            </div>

            <Text size="lg1" className="my-2 text-center font-semibold" data-testid="subheader-configure-intervention">
                {t('Configure Intervention Data')}
            </Text>

            <div className="grid grid-cols-3 gap-3 mt-3" data-testid="section-intervention-granularity-breakdown">
                <div>
                    <div>
                        <Text size="15" className="text-black font-medium" data-testid="label-intervention-granularity">
                            {t('Select Granularity')}
                        </Text>
                        <Text size="15" className="!text-[#464E5F] mb-2 italic" data-testid="desc-intervention-granularity">
                            {t('Please select the column which represents the Granularity')} <span className="text-red-500">*</span>
                        </Text>
                    </div>

                    <div>
                        <Controller
                            name="interventionGranularity"
                            control={control}
                            rules={{ required: t('Please select') }}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    value={value}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('interventionGranularity')
                                        trackEvent('measurement_mte_configure_intervention_granularity_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                    }}
                                    options={getInterventionColumns(['string_columns', 'numeric_columns'])}
                                    error={errors?.interventionGranularity?.message as string}
                                    data-testid="input-intervention-granularity-dropdown"
                                />
                            )}
                        />

                        <FieldValidation />
                    </div>
                </div>
                <div>
                    <div>
                        <Text size="15" className="text-black font-medium" data-testid="label-intervention-breakdown">
                            {t('Touch Points (Max 5)')}
                        </Text>
                        <Text size="15" className="!text-[#464E5F] mb-2 italic" data-testid="desc-intervention-breakdown">
                            {t('Select the columns that represent your touch points. Only binary columns (values 0 or 1) are allowed.')}
                        </Text>
                    </div>

                    <div>
                        <Controller
                            name="touchPoints"
                            control={control}
                            rules={{ required: t('Please select') }}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    value={value}
                                    isMulti={true}
                                    max={5}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('touchPoints')
                                        trackEvent('measurement_mte_configure_touch_points_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                    }}
                                    options={getInterventionColumns(['boolean_columns'])}
                                    error={errors?.touchPoints?.message as string}
                                    data-testid="input-touch-points-dropdown"
                                />
                            )}
                        />

                        <FieldValidation />
                    </div>
                </div>
                <div data-testid="section-blocking-factors">
                    <div>
                        <Text size="15" className="text-black font-medium" data-testid="label-blocking-factors">
                            {t('Select Blocking Factor (Max 5)')}
                        </Text>
                        <Text size="15" className="!text-[#464E5F] mb-2 italic" data-testid="desc-blocking-factors">
                            {t('These are factors which can bias the result. Ex: If expt. is a marketing campaign, customer Demographic must be equally distributed in the AB test.')}
                        </Text>
                    </div>

                    <div>
                        <Controller
                            name="blockingFactors"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    value={value}
                                    isMulti
                                    isClearable
                                    disabled={true}
                                    max={5}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('blockingFactors')
                                        trackEvent('measurement_mte_configure_blocking_factors_selected', { experiment_literal: experiment?.experiment_literal })
                                    }}
                                    options={getInterventionColumns(['string_columns'])}
                                    error={errors?.blockingFactors?.message as string}
                                    data-testid="input-blocking-factors-dropdown"
                                />
                            )}
                        />

                        <FieldValidation />
                    </div>
                </div>
            </div>

            <div className="mt-3" data-testid="section-intervention-details-header">
                {/* <Text size="19" className="font-semibold">
                    {t('Intervention Details')}
                </Text>
                <Text size="15" className="!text-[#464E5F] !text-[0.625rem] italic">
                    {t('Please select the column(S) to be mapped with the respective Intervention')}
                </Text> */}
            </div>

            <div className="mt-3 grid grid-cols-3 gap-3" data-testid="section-intervention-details-inputs">
                {interventions?.map((intervention: any, index: number) => (
                    <div key={`intervention-${intervention.id}`} data-testid={`intervention-row-${index}`}>
                        <Label text={intervention.name} required={true} data-testid={`label-intervention-name-${index}`} />

                        <div>
                            <Controller
                                name={`interventions[${index}].value`}
                                control={control}
                                rules={{ required: t('Please select') }}
                                render={({ field: { value, onChange } }) => (
                                    <Dropdown
                                        value={value}
                                        onChange={(value: any) => {
                                            onChange(value)
                                            void trigger(`interventions.${index}`)
                                            trackEvent('measurement_mte_configure_intervention_mapped', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                        }}
                                        options={getInterventionColumns(['all'])}
                                        error={(errors?.interventions as any)?.[index]?.value?.message as string}
                                        data-testid={`input-intervention-dropdown-${index}`}
                                    />
                                )}
                            />

                            <FieldValidation />
                        </div>
                    </div>
                ))}
            </div>

            <p className="mt-4 text-[1rem] font-bold" data-testid="header-intervention-data">
                {t('Intervention Data:')}
            </p>

            <div className="mt-2" data-testid="intervention-data-grid">
                <CustomAgGrid
                    key={'mte2_' + i18n.language}
                    gridRef={dataTableGrid}
                    rowData={InterventionRowData}
                    columnDefs={InterventionColumnDefs}
                    defaultColDef={{ sortable: true, filter: true }}
                    theme="light"
                    pagination={false}
                    paginationPageSize={5}
                    paginationPageSizeSelector={[5, 10, 20, 50]}
                    style={{ height: 350, width: '100%' }}
                    gridId="summmyGrid"
                    rowHeight={50}
                    alternativeRowColor={'#E5B61112'}
                    data-testid="intervention-custom-ag-grid"
                />
            </div>

            <div className="flex justify-between my-3" data-testid="section-impact-interaction">
                {/* <div>
                    <Label text={t('Do you want to consider the Impact of Interactions between Interventions?')} required={true} data-testid="label-impact-interaction" />
                    <Text size="xs" className="!text-[#464E5F]  italic" data-testid="desc-impact-interaction">
                        {t('Note:')}
                        <br />
                        {t(
                            'Note: Interaction Refers to the combined effect of different Interventions (if applicable). Ex: the measuring the joint effect of Digital Campaign with Discounts. This might result in a reduced sample size, and thus might not yield to many significant results. Please choose carefully.',
                        )}
                    </Text>
                </div>
                <div className="flex gap-[0.666rem]">
                    <button
                        type="button"
                        onClick={() => {
                            setImpact(false)
                            trackEvent('measurement_mte_configure_impact_no_selected', { experiment_literal: experiment?.experiment_literal })
                        }}
                        className={`${impact ? 'bg-[#74788D] text-white' : 'bg-primary text-black'} font-semibold text-[0.667rem] px-[0.833rem] h-[1.667rem] flex items-center justify-center rounded-[0.333rem]`}
                        data-testid="button-impact-no"
                    >
                        {t('No')}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setImpact(true)
                            trackEvent('measurement_mte_configure_impact_yes_selected', { experiment_literal: experiment?.experiment_literal })
                        }}
                        className={`${impact ? 'bg-primary text-black' : 'bg-[#74788D] text-white'} font-semibold text-[0.667rem] px-[0.833rem] h-[1.667rem] flex items-center justify-center rounded-[0.333rem]`}
                        data-testid="button-impact-yes"
                    >
                        {t('Yes')}
                    </button>
                </div> */}
            </div>

            <div className="flex items-center justify-between gap-3 pt-4" data-testid="section-action-buttons">
                <Button
                    theme="secondary"
                    size="xs"
                    styleType="outline"
                    onClick={() => {
                        trackEvent('measurement_mte_configure_previous_clicked', { experiment_literal: experiment?.experiment_literal })
                        if (onPrevious) {
                            onPrevious()
                        }
                    }}
                    data-testid="button-previous-step"
                >
                    {t('Previous Step')}
                </Button>
                <Button
                    theme="secondary"
                    size="xs"
                    onClick={() => {
                        trackEvent('measurement_mte_configure_submitted', { experiment_literal: experiment?.experiment_literal })
                        void trigger()
                            .then((valid: boolean) => {
                                if (valid) {
                                    updateExperiment()
                                }
                            })
                            .catch((error) => {
                                console.error('Error :', error)
                            })
                    }}
                    disabled={working}
                    data-testid="button-measure-result"
                >
                    {working ? <Loader data-testid="loader-working" /> : t('Measure Result')}
                </Button>
            </div>
            <MeasurementSuccessModal _ref={measurementSuccessModalRef} mdata={mdata} data-testid="measurement-success-modal" updateExperiment={true} />
        </div>
    )
}

export default MTEMeasurementConfigure
